import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  Loader2, TrendingUp, Package, Store, Wrench,
  Users, FileText, AlertTriangle, DollarSign, Truck, Clock, ArrowUpRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const DONUT_COLORS = ['var(--primary)', 'var(--success)', '#F59E0B', '#A855F7', '#06B6D4', '#F97316'];

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-lg border shadow-xl" style={{ background: 'rgba(10,15,26,0.95)', borderColor: 'var(--border)', backdropFilter: 'blur(12px)' }}>
      <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider mb-1" style={{ color: 'var(--text-faint)' }}>{label}</p>
      <p className="text-sm font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--primary)' }}>
        {payload[0]?.value} uses
      </p>
      {payload[0]?.payload?.total_value > 0 && (
        <p className="text-xs font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>
          {fmt(payload[0]?.payload?.total_value)} total
        </p>
      )}
    </div>
  );
};

const CustomDonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-lg border shadow-xl" style={{ background: 'rgba(10,15,26,0.95)', borderColor: 'var(--border)', backdropFilter: 'blur(12px)' }}>
      <p className="text-xs font-['IBM_Plex_Sans'] font-medium text-white">{payload[0]?.name}</p>
      <p className="text-sm font-['JetBrains_Mono'] font-bold" style={{ color: payload[0]?.payload?.fill }}>
        {payload[0]?.value} repairs
      </p>
      <p className="text-[10px] font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>
        avg {fmt(payload[0]?.payload?.avg_cost)}
      </p>
    </div>
  );
};

export default function Statistics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/ops-analytics/statistics');
        setData(res.data);
      } catch { toast.error('Failed to load statistics'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="animate-spin" size={28} style={{ color: 'var(--primary)' }} />
        <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>Loading Analytics...</p>
      </div>
    </div>
  );

  if (!data) return <p className="text-center py-12 text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No data available</p>;

  const { summary, parts_most_used = [], vendors_most_used = [], categories = [], top_customers = [] } = data;

  const partsChartData = parts_most_used.slice(0, 8).map(p => ({
    name: (p._id || 'Unknown').length > 18 ? (p._id || 'Unknown').slice(0, 16) + '…' : (p._id || 'Unknown'),
    fullName: p._id || 'Unknown',
    count: p.count,
    total_value: p.total_value || 0,
  }));

  const categoriesChartData = categories.slice(0, 6).map(c => ({
    name: c._id || 'Other',
    value: c.count,
    avg_cost: c.avg_cost || 0,
  }));

  const kpis = [
    { label: 'Customers',  value: summary.total_customers,  icon: Users,          color: 'var(--primary)', glow: false, link: '/ops/customers' },
    { label: 'Fleet Units', value: summary.total_trucks,    icon: Truck,          color: 'var(--primary)', glow: false, link: '/ops/trucks' },
    { label: 'Repairs',    value: summary.total_repairs,    icon: Wrench,         color: '#A855F7',         glow: false, link: '/ops/repairs' },
    { label: 'Invoices',   value: summary.total_invoices,   icon: FileText,       color: 'var(--primary)', glow: false, link: '/ops/invoices' },
    { label: 'Unpaid',     value: summary.unpaid_invoices,  icon: Clock,          color: 'var(--warning)', glow: summary.unpaid_invoices > 0,  link: '/ops/invoices?payment_status=pending' },
    { label: 'Overdue',    value: summary.overdue_invoices, icon: AlertTriangle, color: 'var(--error)',    glow: summary.overdue_invoices > 0, link: '/ops/invoices?payment_status=overdue' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-dashboard">

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] mb-1" style={{ color: 'var(--text-faint)' }}>// ANALYTICS</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl tracking-tight" style={{ color: 'var(--text-primary)' }}>Business Statistics</h1>
        </div>
        <p className="text-[10px] font-['JetBrains_Mono'] hidden md:block" style={{ color: 'var(--text-faint)' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* KPI Cards — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-kpi-cards">
        {kpis.map((k) => (
          <button
            key={k.label}
            onClick={() => navigate(k.link)}
            data-testid={`kpi-card-${k.label.toLowerCase().replace(' ', '-')}`}
            className="group relative overflow-hidden rounded-xl border p-4 flex flex-col justify-between transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl text-left cursor-pointer"
            style={{
              background: 'var(--surface)',
              borderColor: k.glow ? k.color + '60' : 'var(--border)',
              boxShadow: k.glow ? `0 0 20px ${k.color}20` : '0 4px 16px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = k.color; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = k.glow ? k.color + '60' : 'var(--border)'; }}>
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: k.color, opacity: k.glow ? 1 : 0.3 }} />
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>{k.label}</p>
              <k.icon size={13} style={{ color: k.color }} strokeWidth={2} />
            </div>
            <div className="flex items-end justify-between">
              <p className="font-['JetBrains_Mono'] text-3xl font-light tracking-tighter" style={{ color: k.glow ? k.color : 'var(--text-primary)' }}>
                {k.value ?? 0}
              </p>
              <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: k.color }} />
            </div>
          </button>
        ))}
      </div>

      {/* Charts Row: Parts Bar + Categories Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Parts Most Used — Bar Chart */}
        <div
          data-testid="chart-parts-most-used"
          className="lg:col-span-2 rounded-xl border p-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <div className="flex items-center gap-2 mb-5">
            <Package size={14} style={{ color: 'var(--primary)' }} />
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// Parts Most Used</p>
          </div>
          {partsChartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center">
              <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No parts data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={partsChartData} margin={{ left: -16, right: 8, top: 4, bottom: 40 }} barSize={24}>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  angle={-35}
                  textAnchor="end"
                  tick={{ fill: 'var(--text-faint)', fontSize: 9, fontFamily: 'JetBrains Mono' }}
                  interval={0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--text-faint)', fontSize: 9, fontFamily: 'JetBrains Mono' }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
                <Bar dataKey="count" fill="var(--primary)" radius={[4, 4, 0, 0]}>
                  {partsChartData.map((_, i) => (
                    <Cell key={i} fill={`rgba(59,130,246,${1 - i * 0.08})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Repair Categories — Donut */}
        <div
          data-testid="chart-repair-categories"
          className="rounded-xl border p-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <div className="flex items-center gap-2 mb-5">
            <Wrench size={14} style={{ color: '#A855F7' }} />
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// Repair Categories</p>
          </div>
          {categoriesChartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center">
              <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-faint)' }}>No repair data yet</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie
                    data={categoriesChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={75}
                    dataKey="value"
                    paddingAngle={3}
                    strokeWidth={0}>
                    {categoriesChartData.map((_, i) => (
                      <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomDonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="space-y-1.5 mt-1">
                {categoriesChartData.map((c, i) => (
                  <div key={c.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="text-xs font-['IBM_Plex_Sans'] truncate" style={{ color: 'var(--text-muted)' }}>{c.name}</span>
                    </div>
                    <span className="text-[10px] font-['JetBrains_Mono'] font-bold ml-2" style={{ color: 'var(--text-primary)' }}>{c.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Leaderboard Row: Vendors + Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Vendors Most Used — Leaderboard */}
        <div
          data-testid="vendors-most-used"
          className="rounded-xl border p-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <div className="flex items-center gap-2 mb-5">
            <Store size={14} style={{ color: 'var(--primary)' }} />
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// Vendors Most Used</p>
          </div>
          {vendors_most_used.length === 0 ? (
            <p className="text-xs font-['IBM_Plex_Sans'] py-4" style={{ color: 'var(--text-faint)' }}>No vendor data yet</p>
          ) : (
            <div className="space-y-1">
              {vendors_most_used.slice(0, 8).map((v, i) => {
                const maxCost = vendors_most_used[0]?.total_cost || 1;
                const pct = Math.max(4, (v.total_cost / maxCost) * 100);
                return (
                  <button key={i} data-testid={`list-item-vendors-${i}`}
                    onClick={() => navigate(`/ops/vendors?search=${encodeURIComponent(v._id || '')}`)}
                    title={`Open vendors filtered by "${v._id || 'Unknown'}"`}
                    className="w-full relative flex items-center gap-3 px-3 py-2.5 rounded-lg overflow-hidden transition-all hover:translate-x-0.5 cursor-pointer text-left"
                    style={{ borderColor: 'transparent' }}>
                    {/* Background fill bar */}
                    <div className="absolute inset-0 rounded-lg" style={{ background: `rgba(59,130,246,0.06)`, width: `${pct}%`, transition: 'width 0.6s ease' }} />
                    {/* Rank */}
                    <span className="relative text-[10px] font-['JetBrains_Mono'] w-5 flex-shrink-0 text-center font-bold"
                      style={{ color: i === 0 ? 'var(--primary)' : 'var(--text-faint)' }}>
                      {i + 1}
                    </span>
                    {/* Name */}
                    <p className="relative flex-1 text-sm font-['IBM_Plex_Sans'] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {v._id || 'Unknown'}
                    </p>
                    {/* Stats */}
                    <div className="relative text-right flex-shrink-0">
                      <p className="text-xs font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--primary)' }}>{fmt(v.total_cost)}</p>
                      <p className="text-[9px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>{v.repair_count} repair{v.repair_count !== 1 ? 's' : ''}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Customers by Revenue */}
        <div
          data-testid="top-customers"
          className="rounded-xl border p-5"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp size={14} style={{ color: 'var(--success)' }} />
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// Top Customers by Revenue</p>
          </div>
          {top_customers.length === 0 ? (
            <p className="text-xs font-['IBM_Plex_Sans'] py-4" style={{ color: 'var(--text-faint)' }}>No invoice data yet</p>
          ) : (
            <div className="space-y-1">
              {top_customers.slice(0, 8).map((c, i) => {
                const maxVal = top_customers[0]?.total_billed || 1;
                const pct = Math.max(4, (c.total_billed / maxVal) * 100);
                return (
                  <button key={i} data-testid={`list-item-customers-${i}`}
                    onClick={() => navigate(c.customer_id ? `/ops/customers/${c.customer_id}` : `/ops/customers?search=${encodeURIComponent(c.company_name || '')}`)}
                    title={`Open ${c.company_name}`}
                    className="w-full relative flex items-center gap-3 px-3 py-2.5 rounded-lg overflow-hidden transition-all hover:translate-x-0.5 cursor-pointer text-left">
                    {/* Background fill bar */}
                    <div className="absolute inset-0 rounded-lg" style={{ background: `rgba(34,197,94,0.07)`, width: `${pct}%`, transition: 'width 0.6s ease' }} />
                    {/* Rank */}
                    <span className="relative text-[10px] font-['JetBrains_Mono'] w-5 flex-shrink-0 text-center font-bold"
                      style={{ color: i === 0 ? 'var(--success)' : 'var(--text-faint)' }}>
                      {i + 1}
                    </span>
                    {/* Name + badge */}
                    <div className="relative flex-1 min-w-0">
                      <p className="text-sm font-['IBM_Plex_Sans'] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {c.company_name}
                      </p>
                      <p className="text-[9px] font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                        {c.invoice_count} invoice{c.invoice_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {/* Revenue */}
                    <p className="relative text-sm font-['JetBrains_Mono'] font-bold flex-shrink-0" style={{ color: 'var(--success)' }}>
                      {fmt(c.total_billed)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
